import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Rss, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const FEED_LIBRARY = [
  { topic: "Learning English", feeds: [
    { name: "Word of the Day (Merriam-Webster)", url: "https://www.merriam-webster.com/wotd/feed/rss2" },
    { name: "Espresso English", url: "https://www.espressoenglish.net/feed" },
    { name: "Grammar Girl", url: "https://feeds.simplecast.com/XcH2p3Ah" },
  ]},
  { topic: "Learning AI", feeds: [
    { name: "Google AI Blog", url: "https://blog.google/technology/ai/rss/" },
    { name: "OpenAI News", url: "https://openai.com/news/rss.xml" },
    { name: "Hugging Face Blog", url: "https://huggingface.co/blog/feed.xml" },
    { name: "Machine Learning Mastery", url: "https://machinelearningmastery.com/feed/" },
  ]},
  { topic: "General Knowledge", feeds: [
    { name: "Stuff You Should Know", url: "https://www.howstuffworks.com/podcasts/stuff-you-should-know.rss" },
    { name: "TED Talks Daily", url: "https://feeds.feedburner.com/TEDTalks_audio" },
    { name: "ScienceDaily Top Science", url: "https://www.sciencedaily.com/rss/top/science.xml" },
    { name: "NASA News", url: "https://www.nasa.gov/feed/" },
  ]},
  { topic: "Digital Marketing", feeds: [
    { name: "Search Engine Journal", url: "https://www.searchenginejournal.com/feed/" },
    { name: "Neil Patel", url: "https://neilpatel.com/blog/feed/" },
    { name: "HubSpot Marketing", url: "https://blog.hubspot.com/marketing/rss.xml" },
    { name: "Social Media Examiner", url: "https://www.socialmediaexaminer.com/feed/" },
    { name: "Moz Blog", url: "https://moz.com/posts/rss/blog" },
  ]},
  { topic: "Tech", feeds: [
    { name: "TechCrunch", url: "https://techcrunch.com/feed/" },
    { name: "The Verge", url: "https://www.theverge.com/rss/index.xml" },
    { name: "Hacker News", url: "https://news.ycombinator.com/rss" },
  ]},
];

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const DEFAULT_URL = FEED_LIBRARY[0].feeds[0].url;

export default function ContentCard({ card, onData }) {
  const d = card.data || {};
  const feedUrl = d.feed_url || DEFAULT_URL;
  const [customOpen, setCustomOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState("");

  const { data: items = [], isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["content-feed", feedUrl],
    queryFn: () => api.get("/feed", { params: { url: feedUrl } }).then((r) => r.data),
    refetchInterval: 900000,
    staleTime: 600000,
    retry: 1,
  });

  const selectFeed = (val) => {
    if (val === "__custom") { setCustomOpen(true); return; }
    const feed = FEED_LIBRARY.flatMap((t) => t.feeds).find((f) => f.url === val);
    onData({ feed_url: val, feed_name: feed?.name || "Custom feed" });
  };

  const saveCustom = () => {
    let u = customUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    onData({ feed_url: u, feed_name: "Custom feed" });
    setCustomOpen(false);
    setCustomUrl("");
    toast("Feed added — loading ✦");
  };

  const isCustom = !FEED_LIBRARY.flatMap((t) => t.feeds).some((f) => f.url === feedUrl);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <select data-testid="content-feed-select" value={isCustom ? "__current_custom" : feedUrl}
          onChange={(e) => selectFeed(e.target.value)}
          className="min-w-0 flex-1 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1.5 text-xs outline-none cursor-pointer">
          {isCustom && <option value="__current_custom">🔗 {d.feed_name || "Custom feed"}</option>}
          {FEED_LIBRARY.map((t) => (
            <optgroup key={t.topic} label={t.topic}>
              {t.feeds.map((f) => <option key={f.url} value={f.url}>{f.name}</option>)}
            </optgroup>
          ))}
          <option value="__custom">🔗 Custom RSS URL...</option>
        </select>
        <button data-testid="content-refresh-button" onClick={() => refetch()} title="Refresh"
          className="rounded-lg bg-black/10 dark:bg-white/10 p-1.5 opacity-60 transition-opacity hover:opacity-100">
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {customOpen && (
        <div className="space-y-1.5 rounded-xl bg-black/10 dark:bg-white/10 p-2" data-testid="content-custom-panel">
          <input data-testid="content-custom-url-input" value={customUrl} onChange={(e) => setCustomUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveCustom()}
            placeholder="https://example.com/feed.xml" autoFocus
            className="w-full rounded-lg bg-white/60 dark:bg-black/30 px-2 py-1.5 text-xs outline-none placeholder:opacity-50" />
          <div className="flex gap-1.5">
            <button data-testid="content-custom-save" onClick={saveCustom}
              className="flex-1 rounded-lg bg-violet-500 py-1.5 text-xs font-bold text-white">Add feed ✦</button>
            <button data-testid="content-custom-cancel" onClick={() => setCustomOpen(false)}
              className="rounded-lg bg-black/10 dark:bg-white/10 px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-1.5 overflow-y-auto" data-testid="content-list"
        style={{ height: card?.h ? Math.max(160, card.h - 110) : 280 }}>
        {isLoading && <div className="pt-20 text-center"><div className="nebula-loader mx-auto" /></div>}
        {isError && <p className="pt-16 text-center font-hand text-xl opacity-50">couldn't read this feed — try another ✦</p>}
        {items.map((n, i) => (
          <a key={i} href={n.link} target="_blank" rel="noopener noreferrer"
            data-testid={`content-item-${i}`}
            className="group block rounded-xl bg-white/40 dark:bg-black/25 p-2 transition-colors hover:bg-white/70 dark:hover:bg-black/40">
            <p className="text-xs font-semibold leading-snug line-clamp-2 group-hover:underline">{n.title}</p>
            {n.description && <p className="mt-0.5 text-[11px] leading-snug opacity-60 line-clamp-2">{n.description}</p>}
            <div className="mt-1 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide opacity-50">
              <Rss className="h-2.5 w-2.5" /> {n.published && timeAgo(n.published)}
              <ExternalLink className="ml-auto h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
